/** @jsxImportSource solid-js */

import { Component, Show, createSignal, untrack } from 'solid-js';
import {
  useCommentApi,
  useCommentState,
  IComment,
  IViewpoint,
  ICommentAttachments,
  ICommentAttachmentDetails,
} from '@tc-comments/providers';
import {
  listItemsinaFolder,
  CreateViewRequest,
} from '@data-access/connect/tc-tcps';
import { Client, createClient } from '@hey-api/client-fetch';
import { ErrorBoundary } from 'solid-js';
import { translate } from '@fulcrum/lib';
import CommentAttachmentList from '../AttachmentList/CommentAttachmentList';
import { AttachmentPicker, IAttachedFileList } from '@tc-attachment-picker/ui';

export interface ICommentData {
  readonly comment: string;
  readonly attachments: ICommentAttachments[];
  readonly attachmentsDetails: ICommentAttachmentDetails[];
  readonly viewpoint?: IViewpoint;
  /** Full view creation request captured from the host, ready to be saved to the backend. */
  readonly createViewRequest?: CreateViewRequest;
}

// Raw viewpoint data returned from the callback (before transformation to IViewpoint)
export interface IViewpointData {
  readonly viewSnapshot?: string;
  readonly cameraDetail?: {
    readonly position?: {
      readonly x: number;
      readonly y: number;
      readonly z: number;
    };
    readonly target?: {
      readonly x: number;
      readonly y: number;
      readonly z: number;
    };
    readonly up?: {
      readonly x: number;
      readonly y: number;
      readonly z: number;
    };
    readonly fov?: number;
    readonly near?: number;
    readonly far?: number;
  };
  readonly modelDetail?: unknown;
  readonly sectionPlaneDetail?: unknown;
  readonly lineMarkups?: unknown;
}

export interface IAddCommentProps {
  readonly projectId: string;
  readonly objectId: string | undefined;
  onCommentAdded?(comment: IComment): void;
  onCommentDataChange?(commentData: ICommentData): void;
  readonly maxHeight: number;
  readonly userTiduuid: string;
  readonly embedIn3D: boolean;
  refreshViewpointFn(): void;
  readonly objectType: string;
  readonly contextId: string | undefined;
  readonly isEditMode?: boolean;
  readonly shouldEmitData?: boolean;
  onAddIssueCommentView?(): Promise<CreateViewRequest | null>;
  /**
   * Called after the comment is created (standalone mode) with the comment ID and
   * the captured view creation request. The handler should create the view and
   * attach it to the comment.
   */
  onCreateCommentView?(
    commentId: string,
    viewRequest: CreateViewRequest
  ): Promise<void>;
  // Note: accessToken and tcpsApiDomain are now accessed via useCommentState()
}

// Internal component that requires provider context
const AddCommentInternal: Component<IAddCommentProps> = (props) => {
  // Use comment providers - these will throw if not in provider context
  const commentApi = useCommentApi();
  const commentState = useCommentState();

  // Get provider data for AttachmentPicker
  const state = commentState();
  const accessToken = state.accessToken;
  const tcpsApiDomain = state.tcpsApiDomain;

  // Create properly configured client for API calls
  const apiClient: Client = createClient({
    baseUrl: tcpsApiDomain || window.location.origin,
    global: false,
  });

  // Configure request interceptor for authentication
  apiClient.interceptors.request.use((request) => {
    request.headers.set('Authorization', `Bearer ${accessToken}`);
    request.headers.set('Content-Type', 'application/json');
    return request;
  });
  const createCommentMutation = commentApi.createCommentMutation();
  const createCommentAttachmentsMutation =
    commentApi.createCommentAttachmentsMutation();
  // Keep deprecated mutation for backward compatibility during transition
  const createFileAttachmentMutation =
    commentApi.createFileAttachmentMutation();

  const [isCommentModeActive, setIsCommentModeActive] = createSignal(
    untrack(() => (props.isEditMode ? true : false))
  );
  const [displayMore, setDisplayMore] = createSignal(false);
  const [addCommentFailed, setAddCommentFailed] = createSignal(false);
  const [showAttachments, setShowAttachments] = createSignal(false);
  const [comment, setComment] = createSignal('');
  const [attachments, setAttachments] = createSignal<ICommentAttachments[]>([]);
  const [attachmentsDetails, setAttachmentsDetails] = createSignal<
    ICommentAttachmentDetails[]
  >([]);
  const [uploadingAttachment, setUploadingAttachment] = createSignal(false);
  const [viewCreateRequest, setViewCreateRequest] = createSignal<
    CreateViewRequest | undefined
  >(undefined);

  const commentStepHeight = 16;
  const attachmentHeightMultiplier = 2;

  // Helper function to emit current comment data
  const emitCommentData = (): void => {
    if (props.onCommentDataChange && props.shouldEmitData) {
      // Only emit data when shouldEmitData is true (create/edit modes)
      const commentData: ICommentData = {
        comment: comment(),
        attachments: attachments(),
        attachmentsDetails: attachmentsDetails(),
        viewpoint: undefined,
        createViewRequest: viewCreateRequest(),
      };
      props.onCommentDataChange(commentData);
    }
  };

  const enterCommentMode = (event: MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();

    setIsCommentModeActive(true);

    const customEvent = new CustomEvent('comment-mode-entered', {
      detail: { originalEvent: event },
      bubbles: true,
    });
    (event.target as HTMLElement)?.dispatchEvent(customEvent);
  };

  const reset = (): void => {
    setIsCommentModeActive(false);
    setComment('');
    setAddCommentFailed(false);
    setAttachments([]);
    setAttachmentsDetails([]);
    setViewCreateRequest(undefined);

    const addCommentElement = document.getElementById(
      'topic-comment-add'
    ) as HTMLTextAreaElement;
    if (addCommentElement) {
      setElementHeight(addCommentElement);
    }

    const customEvent = new CustomEvent('comment-mode-reset', {
      detail: {},
      bubbles: true,
    });
    document.dispatchEvent(customEvent);
  };

  const getAttachmentHeight = (): number => {
    const attachmentList = attachments();
    const minAttachmentListSize = 3;
    const staticCommentHeight = 40;
    const attachmentHeightOffset = 2;
    const minAttachmentHeightOffset = 1;

    if (attachmentList.length > 0) {
      if (attachmentList.length > minAttachmentListSize) {
        if (displayMore()) {
          return (
            (attachmentList.length + attachmentHeightOffset) *
            staticCommentHeight
          );
        } else {
          return (
            (minAttachmentListSize + minAttachmentHeightOffset) *
            staticCommentHeight
          );
        }
      } else {
        return (
          (attachmentList.length + minAttachmentHeightOffset) *
          staticCommentHeight
        );
      }
    }
    return commentStepHeight * attachmentHeightMultiplier;
  };

  const setElementHeight = (element: HTMLTextAreaElement): void => {
    const commentSupportDiv = document.getElementById('comment-container');
    const commentWrapperDiv = document.getElementById('add-comment-wrapper');
    const commentHeightMultiplier = 3;
    if (commentSupportDiv && commentWrapperDiv) {
      // commentSupportDiv.innerHTML = _text;
      const commentHeightValue =
        commentSupportDiv.scrollHeight +
        commentStepHeight * commentHeightMultiplier;
      const newHeight = commentHeightValue + getAttachmentHeight();
      if (newHeight < props.maxHeight) {
        commentWrapperDiv.style.height = newHeight + 'px';
      }
      element.style.height = commentHeightValue + 'px';
    }
  };

  const saveComment = async (): Promise<void> => {
    try {
      if (comment() !== '') {
        setAddCommentFailed(false);

        // Emit custom event for comment save
        const customEvent = new CustomEvent('comment-save-requested', {
          detail: {
            comment: comment(),
            attachments: attachments(),
            attachmentsDetails: attachmentsDetails(),
            projectId: props.projectId,
            objectId: props.objectId,
          },
          bubbles: true,
        });
        document.dispatchEvent(customEvent);

        // Use the comment API to create the comment
        const result = await createCommentMutation.mutateAsync({
          comment: comment(),
          objectId: props.objectId,
          projectId: props.projectId,
          objectType: props.objectType,
          contextId: props.contextId,
        });

        let finalComment = result.comment;

        // Handle attachments after comment creation using new API
        const currentAttachments = attachments();

        if (currentAttachments.length > 0) {
          const fileAttachments = currentAttachments.filter(
            (att) => att.type === 'FILE'
          );

          if (fileAttachments.length > 0) {
            try {
              const attachmentRequests = fileAttachments.map((attachment) => ({
                type: attachment.type,
                id: attachment.id,
              }));

              await createCommentAttachmentsMutation.mutateAsync({
                commentId: result.comment.id,
                attachments: attachmentRequests,
              });

              finalComment = {
                ...result.comment,
                hasAttachment: true,
              };
            } catch (attachmentError) {
              console.error('Error creating attachments:', attachmentError);
              finalComment = result.comment;
            }
          } else {
            finalComment = result.comment;
          }
        }

        // Save the captured view and attach it to the comment
        const pendingViewRequest = viewCreateRequest();
        if (pendingViewRequest && props.onCreateCommentView) {
          try {
            await props.onCreateCommentView(
              result.comment.id,
              pendingViewRequest
            );
            finalComment = { ...finalComment, hasAttachment: true };
          } catch (viewError) {
            console.error('Error creating comment view:', viewError);
          }
        }

        // Call props callback if provided
        if (props.onCommentAdded && finalComment) {
          props.onCommentAdded(finalComment);
        }

        // Reset form
        reset();

        // Emit success event
        const successEvent = new CustomEvent('comment-saved', {
          detail: {
            comment: finalComment,
            success: true,
          },
          bubbles: true,
        });
        document.dispatchEvent(successEvent);
      }
    } catch (error) {
      console.error('Error while adding new comment:', error);
      setAddCommentFailed(true);
    }
  };

  const commentTextChanged = (event: Event): void => {
    const element = event.currentTarget as HTMLTextAreaElement;
    setElementHeight(element);
    setComment(element.value);
    // Emit comment data change in real-time
    setTimeout(() => emitCommentData(), 0);
  };

  const openAttachmentPicker = (): void => {
    setShowAttachments(false);
    setTimeout(() => setShowAttachments(true), 0);

    const customEvent = new CustomEvent('attachment-picker-requested', {
      detail: {},
      bubbles: true,
    });
    document.dispatchEvent(customEvent);
  };

  const addViewpointToComment = async (): Promise<void> => {
    // Use callback - required for viewpoint capture
    if (!props.onAddIssueCommentView) {
      console.warn('No viewpoint capture callback provided');
      return;
    }

    setUploadingAttachment(true);

    const customEvent = new CustomEvent('viewpoint-capture-requested', {
      detail: {
        projectId: props.projectId,
        objectId: props.objectId,
      },
      bubbles: true,
    });
    document.dispatchEvent(customEvent);

    try {
      // Use the provided callback to get the full view creation request from the host
      const createViewReq = await props.onAddIssueCommentView();

      if (!createViewReq) {
        throw new Error('Failed to capture viewpoint data');
      }

      const thumbnail =
        createViewReq.imageData ||
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==';

      const newAttachment: ICommentAttachments = {
        title: 'Current View',
        type: 'VIEW',
        imgUrl: thumbnail,
        id: 'view-' + Date.now(),
      };

      setAttachments([newAttachment]);
      setViewCreateRequest(createViewReq);
      setUploadingAttachment(false);
      setShowAttachments(false);

      props.refreshViewpointFn();

      // Emit comment data with the view creation request (create/edit modes)
      setTimeout(() => {
        if (props.onCommentDataChange && props.shouldEmitData) {
          const commentData: ICommentData = {
            comment: comment(),
            attachments: attachments(),
            attachmentsDetails: attachmentsDetails(),
            viewpoint: undefined,
            createViewRequest: createViewReq,
          };
          props.onCommentDataChange(commentData);
        }
      }, 0);
    } catch (error) {
      console.error('Error capturing viewpoint:', error);
      setUploadingAttachment(false);

      // Fallback to placeholder thumbnail only (no view request)
      const newAttachment: ICommentAttachments = {
        title: 'Current View',
        type: 'VIEW',
        imgUrl:
          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==',
        id: 'view-' + Date.now(),
      };

      setAttachments([newAttachment]);
      setShowAttachments(false);
    }
  };

  const removeAttachment = (index: number): void => {
    const newAttachments = [...attachments()];
    const newAttachmentsDetails = [...attachmentsDetails()];
    const removedAttachment = newAttachments[index];
    newAttachments.splice(index, 1);
    newAttachmentsDetails.splice(index, 1);
    setAttachments(newAttachments);
    setAttachmentsDetails(newAttachmentsDetails);
    if (removedAttachment?.type === 'VIEW') {
      setViewCreateRequest(undefined);
    }
    // Emit comment data change after attachment removed
    setTimeout(() => emitCommentData(), 0);
  };

  // Utility function to build authenticated images
  const buildAuthImg = async (url: string): Promise<string> => {
    try {
      const authToken = accessToken ? `Bearer ${accessToken}` : '';

      const response = await fetch(url, {
        method: 'GET',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        headers: authToken ? { Authorization: authToken } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to fetch authenticated image');
      }

      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('buildAuthImg error:', error);
      throw error;
    }
  };

  // Utility function to convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix to get just the base64 data
        const base64Data = result.replace('data:', '').replace(/^.+,/, '');
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleAttachmentAdded = async (
    filesList: IAttachedFileList
  ): Promise<void> => {
    const attachment = filesList;
    const attachmentDetail = attachment.selectedObject;
    const currentAttachments = attachments();
    const attachmentsPayload: ICommentAttachments[] = [];
    const attachmentsDetailsPayload: ICommentAttachmentDetails[] = [];

    setUploadingAttachment(true);

    try {
      if (attachment.type === 'FILE_FROM_CONNECT') {
        // Handle files from Trimble Connect
        const connectFiles = Array.isArray(attachmentDetail)
          ? attachmentDetail
          : [attachmentDetail];
        for (const fileObject of connectFiles) {
          // Check for duplicates
          if (currentAttachments.some((att) => att.id === fileObject.id)) {
            continue;
          }

          const imgUrl =
            fileObject.thumbnailUrl?.[0] || fileObject.thumbnail || '';
          const attachmentItem: ICommentAttachments = {
            title: fileObject.name,
            type: 'FILE',
            imgUrl,
            id: fileObject.id,
          };
          attachmentsPayload.push(attachmentItem);

          try {
            const base64ImgUrl = await buildAuthImg(imgUrl);
            const attachmentDetail: ICommentAttachmentDetails = {
              snapshot: {
                snapshotType: fileObject.name.split('.').pop() || 'png',
                snapshotData:
                  typeof base64ImgUrl === 'string'
                    ? base64ImgUrl.split(',')[1]
                    : '',
              },
            };
            attachmentsDetailsPayload.push(attachmentDetail);
          } catch (error) {
            console.error('Error processing thumbnail:', error);
            // Add placeholder details if thumbnail processing fails
            const attachmentDetail: ICommentAttachmentDetails = {
              snapshot: {
                snapshotType: fileObject.name.split('.').pop() || 'png',
                snapshotData: '',
              },
            };
            attachmentsDetailsPayload.push(attachmentDetail);
          }
        }

        // Apply changes after processing all files
        setAttachments([...currentAttachments, ...attachmentsPayload]);
        setAttachmentsDetails([
          ...attachmentsDetails(),
          ...attachmentsDetailsPayload,
        ]);
        setShowAttachments(false);
        setUploadingAttachment(false);

        // Update textarea height
        const textarea = document.getElementById(
          'topic-comment-add'
        ) as HTMLTextAreaElement;
        if (textarea) {
          setElementHeight(textarea);
        }

        // Emit comment data change
        emitCommentData();
      } else if (attachment.type === 'FILE_FROM_COMPUTER') {
        // Handle files from computer upload using CDM upload logic
        try {
          // Import CDM at runtime since it might not be available in all contexts
          const CDM = await import('trimble-connect-sdk');

          // Get project details - we'll need to fetch this from the API
          const apiBaseUrl = tcpsApiDomain || window.location.origin;
          const projectResponse = await fetch(
            `${apiBaseUrl}/projects/${props.projectId}`,
            {
              headers: {
                // eslint-disable-next-line @typescript-eslint/naming-convention -- HTTP header names must match standard format
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (!projectResponse.ok) {
            throw new Error('Failed to fetch project details');
          }

          const project = await projectResponse.json();

          // Get or create default attachment folder
          let defaultFolder;
          try {
            // Try to get existing attachment folder using listItemsinaFolder
            const foldersResponse = await listItemsinaFolder({
              client: apiClient,
              path: { folderId: project.rootId },
            });

            if (foldersResponse.data) {
              defaultFolder = foldersResponse.data.find(
                (folder: { readonly type: string; readonly name: string }) =>
                  folder.type === 'FOLDER' &&
                  (folder.name === 'Attachments' ||
                    folder.name === 'attachments')
              );
            }

            // Create attachment folder if it doesn't exist
            if (!defaultFolder) {
              const createFolderResponse = await fetch(
                `${apiBaseUrl}/folders`,
                {
                  method: 'POST',
                  headers: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    Authorization: `Bearer ${accessToken}`,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    name: 'Attachments',
                    parentId: project.rootId,
                  }),
                }
              );

              if (createFolderResponse.ok) {
                defaultFolder = await createFolderResponse.json();
              } else {
                throw new Error('Failed to create attachment folder');
              }
            }
          } catch (folderError) {
            console.error('Error handling attachment folder:', folderError);
            // Use root folder as fallback
            defaultFolder = { id: project.rootId };
          }

          const selectedFiles = Array.isArray(attachmentDetail)
            ? attachmentDetail
            : [attachmentDetail];

          // Configure TCPS URL for CDM.TCPSClient
          // Extract domain from tcpsApiDomain
          let baseOrigin;
          try {
            const url = new URL(tcpsApiDomain || window.location.origin);
            baseOrigin = url.host;
          } catch (error) {
            console.error('Failed to parse TCPS URL:', tcpsApiDomain, error);
            // Fallback: try to extract domain manually
            let cleanUrl = tcpsApiDomain || window.location.origin;
            if (cleanUrl.includes('/tc/api/')) {
              cleanUrl = cleanUrl.split('/tc/api/')[0];
            }
            // Remove protocol if present
            baseOrigin = cleanUrl.replace(/^https?:\/\//, '');
          }

          // Create project payload for CDM.TCPSClient
          const projectPayload = {
            id: project.id,
            name: project.name,
            rootId: project.rootId,
            origin: baseOrigin,
          };

          // Use CDM.TCPSClient.uploadFileContent for file upload
          interface ServiceResponse<T> {
            readonly data: T;
          }
          interface FileEntry {
            readonly name: string;
            readonly id: string;
            readonly thumbnailUrl?: readonly string[];
            readonly downloadUrl?: string;
          }
          const uploadPromise = new Promise<ServiceResponse<FileEntry>[]>(
            (resolve, reject) => {
              CDM.TCPSClient.uploadFileContent(
                projectPayload,
                selectedFiles,
                defaultFolder.id,
                'FOLDER'
              ).then(
                (fileEntries: ServiceResponse<FileEntry>[]) => {
                  // Process uploaded files and create attachment items
                  fileEntries.forEach(
                    (fileEntry: ServiceResponse<FileEntry>) => {
                      const fileData = fileEntry.data;

                      const attachmentItem: ICommentAttachments = {
                        title: fileData.name,
                        type: 'FILE',
                        imgUrl: fileData.thumbnailUrl?.[0] || '',
                        id: fileData.id,
                      };
                      attachmentsPayload.push(attachmentItem);

                      // Create attachment details
                      const attachmentDetail: ICommentAttachmentDetails = {
                        snapshot: {
                          snapshotType:
                            fileData.name.split('.').pop() || 'file',
                          snapshotData: '', // CDM handles the file storage
                          snapshotUrl: fileData.downloadUrl || '',
                        },
                      };
                      attachmentsDetailsPayload.push(attachmentDetail);
                    }
                  );

                  resolve(fileEntries);
                },
                (error) => {
                  console.error('Failed to upload files:', error);
                  reject(error);
                }
              );
            }
          );

          // Wait for upload to complete
          await uploadPromise;
        } catch (error) {
          console.error('Error processing FILE_FROM_COMPUTER:', error);

          // Fallback to base64 processing if CDM upload fails
          const fallbackFiles = Array.isArray(attachmentDetail)
            ? attachmentDetail
            : [attachmentDetail];
          for (let index = 0; index < fallbackFiles.length; index++) {
            const fileObject = fallbackFiles[index];

            try {
              const base64Data = await fileToBase64(fileObject);

              const attachmentItem: ICommentAttachments = {
                title: fileObject.name,
                type: 'FILE',
                imgUrl: '', // Will be set after upload
                id: `computer-file-${Date.now()}-${index}`,
              };
              attachmentsPayload.push(attachmentItem);

              const attachmentDetail: ICommentAttachmentDetails = {
                snapshot: {
                  snapshotType: fileObject.name.split('.').pop() || 'png',
                  snapshotData: base64Data,
                },
              };
              attachmentsDetailsPayload.push(attachmentDetail);
            } catch (fileError) {
              console.error('Error processing computer file:', fileError);
            }
          }
        }

        // Apply changes after processing all files
        setAttachments([...currentAttachments, ...attachmentsPayload]);
        setAttachmentsDetails([
          ...attachmentsDetails(),
          ...attachmentsDetailsPayload,
        ]);
        setShowAttachments(false);
        setUploadingAttachment(false);

        // Update textarea height
        const textarea = document.getElementById(
          'topic-comment-add'
        ) as HTMLTextAreaElement;
        if (textarea) {
          setElementHeight(textarea);
        }

        // Emit comment data change
        emitCommentData();
      } else {
        // Fallback for other attachment types
        const selectedObjects = Array.isArray(filesList.selectedObject)
          ? filesList.selectedObject
          : [filesList.selectedObject];
        interface FileObject {
          readonly name?: string;
          readonly displayText?: string;
          readonly thumbnailUrl?: readonly string[];
          readonly thumbnail?: string;
          readonly id?: string;
        }
        const newAttachments = selectedObjects.map(
          (file: FileObject, index: number) => ({
            title: file.name || file.displayText || 'Attachment',
            type: filesList.type || 'FILE',
            imgUrl: file.thumbnailUrl?.[0] || file.thumbnail || '',
            id: file.id || `attachment-${Date.now()}-${index}`,
          })
        );

        setAttachments([...currentAttachments, ...newAttachments]);
        setShowAttachments(false);
        setUploadingAttachment(false);

        // Emit comment data change
        setTimeout(() => emitCommentData(), 0);
      }
    } catch (error) {
      console.error('Error processing attachments:', error);
      setUploadingAttachment(false);
      setShowAttachments(false);
    }
  };

  const handleAttachmentPickerClosed = (): void => {
    setShowAttachments(false);
  };

  const commentMaxHeight = (): number => props.maxHeight - commentStepHeight;
  const commentDivHeight = 10;
  const baseEditModeHeight = 100;
  const attachmentItemHeight = 45;
  const minEditModeHeight = 200;

  // Calculate dynamic height for edit mode to accommodate attachments
  const getEditModeHeight = (): number | undefined => {
    if (!props.isEditMode) return undefined;

    const attachmentCount = attachments().length;
    const attachmentHeight = attachmentCount * attachmentItemHeight;

    return Math.max(minEditModeHeight, baseEditModeHeight + attachmentHeight);
  };

  return (
    <div class="add-comment-container">
      <Show when={!isCommentModeActive()}>
        <div class="add-comment-section inactive" onClick={enterCommentMode}>
          <i
            // eslint-disable-next-line @typescript-eslint/naming-convention
            style={{ 'margin-top': '3px' }}
            class="icon-font tc-icon-comment-add"
          />
          <textarea
            id="topic-comment-add"
            class="comment-add mt-1"
            placeholder={translate('TC.Comments.Placeholder')}
            readonly
          />
        </div>
      </Show>

      <Show when={isCommentModeActive()}>
        <div class="add-comment-section active">
          <div
            id="add-comment-wrapper"
            class={`px-2 pt-2 topic-comment-section-height ${
              props.isEditMode ? 'no-footer' : ''
            }`}
            style={{
              // eslint-disable-next-line @typescript-eslint/naming-convention
              'overflow-x': 'hidden',
              // eslint-disable-next-line @typescript-eslint/naming-convention
              'overflow-y':
                commentDivHeight >= commentMaxHeight() ? 'scroll' : 'hidden',
              ...(props.isEditMode && getEditModeHeight()
                ? { height: `${getEditModeHeight()}px` }
                : {}),
            }}
          >
            <div
              id="comment-container"
              class="comment-support"
              // style={{ padding: '0px' }}
            />
            <div class="relative">
              <textarea
                id="topic-comment-add"
                class="comment-add topic-commentbox-height"
                placeholder={translate('TC.Comments.Placeholder')}
                value={comment()}
                onInput={commentTextChanged}
                autofocus
                maxLength={1024}
              />
              <div class="line" />
            </div>

            <CommentAttachmentList
              attachments={attachments()}
              onRemoveAttachment={removeAttachment}
              displayMore={displayMore()}
              setDisplayMore={setDisplayMore}
              uploading={uploadingAttachment()}
              accessToken={accessToken || undefined}
            />

            <Show
              when={
                addCommentFailed() ||
                createCommentMutation.isError ||
                createCommentAttachmentsMutation.isError ||
                createFileAttachmentMutation.isError
              }
            >
              <div class="alert error mb-2 mt-0">
                {createCommentMutation.error?.message ||
                  createCommentAttachmentsMutation.error?.message ||
                  createFileAttachmentMutation.error?.message ||
                  translate('TC.Error.Comments.AddFailed')}
              </div>
            </Show>
          </div>

          <div class="row-distribute px-1 py-1">
            <div class="flex-row">
              <button
                class="button tertiary icon-circle icon-small"
                onClick={openAttachmentPicker}
                type="button"
                title="Attach file"
              >
                <i class="icon-font i16 tc-icon-attach-file" />
              </button>
              <Show when={props.embedIn3D}>
                <button
                  class={`button tertiary icon-circle icon-small ${
                    uploadingAttachment() ? 'disabled' : ''
                  }`}
                  onClick={addViewpointToComment}
                  disabled={uploadingAttachment()}
                  type="button"
                  title="Add current view"
                >
                  <i class="icon-font i16 tc-icon-views-add" />
                </button>
              </Show>
            </div>
            <Show when={!props.isEditMode}>
              <div class="d-flex">
                <button
                  class="button link-secondary"
                  onClick={reset}
                  disabled={uploadingAttachment()}
                  type="button"
                >
                  {translate('TC.Common.Cancel')}
                </button>
                <button
                  class={`button primary ml-2 ${
                    createCommentMutation.isPending ||
                    createCommentAttachmentsMutation.isPending ||
                    createFileAttachmentMutation.isPending
                      ? 'icon-left'
                      : ''
                  }`}
                  disabled={
                    createCommentMutation.isPending ||
                    createCommentAttachmentsMutation.isPending ||
                    createFileAttachmentMutation.isPending ||
                    !comment() ||
                    uploadingAttachment()
                  }
                  onClick={saveComment}
                  type="button"
                >
                  {createCommentMutation.isPending ||
                  createCommentAttachmentsMutation.isPending ||
                  createFileAttachmentMutation.isPending
                    ? translate('TC.Common.Adding') + '...'
                    : translate('TC.Common.Comment')}
                </button>
              </div>
            </Show>
          </div>
        </div>
      </Show>

      <Show when={showAttachments()}>
        <AttachmentPicker
          allowedAttachmentTypes="FILE_FROM_CONNECT,FILE_FROM_COMPUTER"
          inProgressFlag={false}
          onClose={handleAttachmentPickerClosed}
          addAttachment={handleAttachmentAdded}
          allowedFileExtensions={['.jpg', '.png']}
          allowedUploadFileExt={['image/png', 'image/jpeg']}
          disableMultiSelect={true}
          is3DEmbbedded={true}
          projectId={props.projectId}
          accessToken={accessToken || undefined}
          tcpsApiDomain={tcpsApiDomain || undefined}
        />
      </Show>
    </div>
  );
};

// Wrapper component with error boundary
const AddComment: Component<IAddCommentProps> = (props) => {
  return (
    <ErrorBoundary
      fallback={(error) => {
        console.error(
          'AddComment must be used within a CommentContainer:',
          error
        );
        return (
          <div class="error-message">
            AddComment must be used within a CommentContainer
          </div>
        );
      }}
    >
      <AddCommentInternal {...props} />
    </ErrorBoundary>
  );
};

export default AddComment;
